import { Type } from "@sinclair/typebox";
import { digitalSourceTypesDerivedAsFaksimile, digitalSourceTypesDerivedAsFotografi, digitalSourceTypesDerivedAsGrafikk, digitalSourceTypesDerivedAsIllustrasjon, digitalSourceTypesDerivedAsKIGenerert, } from "./nrk-data";
export var IPTC;
(function (IPTC) {
    IPTC.digitalSourceTypes = [
        ...digitalSourceTypesDerivedAsFotografi,
        ...digitalSourceTypesDerivedAsFaksimile,
        ...digitalSourceTypesDerivedAsGrafikk,
        ...digitalSourceTypesDerivedAsIllustrasjon,
        ...digitalSourceTypesDerivedAsKIGenerert,
    ];
    IPTC.DigitalSourceTypeSchema = Type.Union(IPTC.digitalSourceTypes.map((type) => Type.Literal(type)), {
        description: "Select one of the values for identifying the type of the source of the digital image from the controlled vocabulary http://cv.iptc.org/newscodes/digitalsourcetype/",
        $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#digital-source-type",
        title: "XMP/DigitalSourceType",
    });
    IPTC.PlainFieldsSchema = Type.Object({
        /** A shorthand reference for the digital image */
        title: Type.String({
            description: "A shorthand reference for the digital image.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#title",
            title: "IPTC/Object-Name",
        }),
        /** A textual description, including captions, of the image */
        description: Type.String({
            description: "A textual description, including captions, of the image.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#description",
            title: "IPTC/Caption-Abstract",
        }),
        /** A brief textual description of the purpose and meaning of an image */
        altText: Type.String({
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#alt-text-accessibility",
            description: "A brief textual description of the purpose and meaning of an image " +
                "that can be accessed by assistive technology or displayed when the " +
                "image is disabled in the browser. It should not exceed 250 characters. " +
                "Enter text describing the appearance of the image from a visual perspective, " +
                "focusing on details that are relevant to the purpose and meaning of the image.\n\n" +
                "Derived from multilingual value.",
            title: "XMP/AltTextAccessibility",
        }),
        /** Number or identifier for the purpose of improved workflow handling */
        jobId: Type.String({
            description: "Number or identifier for the purpose of improved workflow handling. " +
                "This is a user created identifier related to the job for which the image is supplied.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#job-id",
            title: "IPTC/OriginalTransmissionReference",
        }),
        /** The name of a person or party who has a role in the content supply chain */
        source: Type.String({
            description: "The name of a person or party who has a role in the content supply chain. " +
                "This could be an agency, a member of an agency, an individual or a combination. " +
                "Source could be different from Creator and from the entities in the Copyright Notice.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#source-supply-chain",
            title: "IPTC/Source",
        }),
        /** Optional identifier assigned by the Image Supplier to the image */
        supplierImageId: Type.String({
            description: "Optional identifier assigned by the Image Supplier to the image.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#image-supplier-image-id",
            title: "XMP/ImageSupplierImageID",
        }),
        /** Keywords to express the subject of the image */
        keywords: Type.Array(Type.String(), {
            description: "Keywords to express the subject of the image. Keywords may be free text and don’t have to be taken from a controlled vocabulary. " +
                "Codes from the controlled vocabulary IPTC Subject NewsCodes must go to the 'Subject Code' field.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#keywords",
            title: "IPTC/Keywords",
        }),
        /** Exact name of the sublocation shown in the image */
        location: Type.String({
            description: "Exact name of the sublocation shown in the image.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#location",
            title: "XMP/Location",
        }),
        /** Full name of the country of the location shown in the image. (Not country code) */
        country: Type.String({
            description: "Full name of the country of the location shown in the image. This element is at the top/first level of a top-down geographical hierarchy. " +
                "The full name should be expressed as a verbal name and not as a code, a code should go to the element 'CountryCode'",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#country",
            title: "IPTC/Country",
        }),
        /** A brief synopsis of the caption */
        headline: Type.String({
            description: "A brief synopsis of the caption. Headline is not the same as Title.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#headline",
            title: "IPTC/Headline",
        }),
        /** The type of the source of the digital image */
        digitalSourceType: Type.Optional(IPTC.DigitalSourceTypeSchema),
        /** Name of the organisation which is featured by this image */
        organisationInImageName: Type.Array(Type.String(), {
            description: "Enter the name of the organisation which is featured by this image",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#name-of-organisation-featured-in-the-image",
            title: "XMP/DigitalSourceType",
        }),
        /** Designates the date and optionally the time the content of the image was created rather than
         * the date of the creation of the digital representation */
        mediaCreatedOn: Type.String({
            description: "Designates the date and optionally the time the content of the image was created " +
                "rather than the date of the creation of the digital representation.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#date-created",
            title: "EXIF/CreatedDate",
        }),
    }, {
        description: "Fields where the IPTC format is sufficient and no extra data needs to be stored.",
    });
    IPTC.DerivedFieldsSchema = Type.Object({
        /** Contains the name of the photographer */
        creators: Type.Array(Type.String(), {
            description: "Contains the name of the photographer, but in cases where the photographer should " +
                "not be identified the name of a company or organisation may be appropriate.\n\n" +
                "Derived from data with Nrk authority information.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#creator",
            title: "EXIF/Artist",
        }),
        /** Name of a person shown in the image */
        personInImage: Type.String({
            description: "Name of a person shown in the image.\n\n" +
                "Derived from data with Nrk authority information.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#person-shown-in-the-image",
            title: "XMP/PersonInImage",
        }),
        /** Contains any necessary copyright notice for claiming the intellectual property for this photograph */
        copyrightNotice: Type.String({
            description: "Contains any necessary copyright notice for claiming the intellectual property for this photograph " +
                "and should identify the current owner of the copyright for the photograph. Other entities like the " +
                "creator of the photograph may be added in the corresponding field. Notes on usage rights should be" +
                "provided in 'Rights usage terms'.\n\n" +
                "Derived from rights object",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#copyright-notice",
            title: "IPTC/CopyrightNotice",
        }),
        /** The licensing parameters of the image expressed in free-text */
        usageTerms: Type.String({
            description: "The licensing parameters of the image expressed in free-text.\n\n" +
                "Derived from rights object",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#rights-usage-terms",
            title: "XMP/UsageTerms",
        }),
        /** The credit to person(s) and/or organisation(s) required by the supplier of the image to be used when published*/
        creditLine: Type.String({
            description: "The credit to person(s) and/or organisation(s) required by the supplier " +
                "of the image to be used when published. This is a free-text field.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#credit-line",
            title: "IPTC/Credit",
        }),
        /** Information about embargoes, or other restrictions not covered by the Rights Usage field. Derived from rights object. */
        instructions: Type.String({
            description: "Information about embargoes, or other restrictions not covered by the Rights Usage field." +
                "\n\nDerived from rights object.",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#instructions",
            title: "IPTC/SpecialInstructions",
        }),
        /** Owner or owners of the copyright in the licensed image */
        copyrightOwners: Type.Array(Type.String(), {
            description: "Owner or owners of the copyright in the licensed image.\n\nDerived from rights object",
            $id: "https://iptc.org/std/photometadata/specification/IPTC-PhotoMetadata#copyright-owner",
            title: "XMP/CopyrightOwnerName",
        }),
    }, {
        description: "Fields where the IPTC representation is too simplistic and the master data is stored elsewhere.",
    });
    IPTC.FieldsSchema = Type.Composite([IPTC.PlainFieldsSchema, IPTC.DerivedFieldsSchema]);
})(IPTC || (IPTC = {}));
//# sourceMappingURL=IPTC.js.map